import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSuppressMapBackground } from '../contexts/MapBackgroundContext';
import { buildTileUrl, tileAttribution as buildAttribution } from '../lib/tiles';
import { GOOGLE_MAPS_API_KEY } from '../lib/mapsKey';
import NewProjectWizard from '../components/NewProjectWizard';
import NewHouseDialog from '../components/maison/NewHouseDialog';
import { HudGrainOverlay } from '../components/hud';
import PortfolioPanel from '../components/hub/PortfolioPanel';
import Hub3DMap from '../components/hub/Hub3DMap';
import AddressGeocoderSearch from '../components/hub/AddressGeocoderSearch';
import LocationDropPinCard from '../components/hub/LocationDropPinCard';
import ExplorerPanel from '../components/hub/ExplorerPanel';
import OnboardingDashboard from '../components/OnboardingDashboard';
import {
  Plus,
  PanelRightClose, PanelRightOpen,
  Maximize2, Home, Mountain,
} from 'lucide-react';
import { toast } from 'sonner';

import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/700.css';
import '@fontsource/jetbrains-mono/400.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const THEME_KEY = 'shoebox_theme';

// --- Pin icons (gold for in_progress, emerald for published) ---
const goldIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:16px;height:16px;background:#D9A05B;border-radius:50%;border:2px solid #09090B;box-shadow:0 0 15px rgba(217,160,91,0.6)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const publishedIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:16px;height:16px;background:#10B981;border-radius:50%;border:2px solid #09090B;box-shadow:0 0 15px rgba(16,185,129,0.5)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const DEFAULT_CENTER = [47.5, 4.5];
const DEFAULT_ZOOM = 5;

// Smoothly fly to user's projects extent on load
const FitToPins = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map(p => p.coords));
    if (bounds.isValid()) {
      map.flyToBounds(bounds, { padding: [80, 80], duration: 1.4, maxZoom: 12 });
    }
  }, [points, map]);
  return null;
};

// V14.68.18 (Feb 2026) — HUD widgets grid (react-grid-layout) removed.
// Suppression du grid + 5 widgets (ShoeboxesWidget, ActivityWidget,
// AgendaPlaceholder, ContactsPlaceholder, HudWidget shell) + DEFAULT_LAYOUTS.
// Ces widgets n'étaient accessibles qu'en mode standalone (route /dashboard)
// désormais redirigée vers /studio/dashboard (mode embedded uniquement).
// L'activité récente est déjà présente dans PortfolioPanel; les stats CRM
// sont accessibles via le module Mini-CRM du sidebar.

// V14.68.18 (Feb 2026) — Widgets HUD grid + composants supprimés.
// Voir l historique git (src/_archive supprimé V14.84).

// =====================================================================
// Main HubDashboard — rendu embarqué dans HUDLayout (V14.68.17/18).
// L'ancienne route standalone /dashboard redirige vers /studio/dashboard.
// =====================================================================
const HubDashboard = () => {
  const { user, logout, updateTheme } = useAuth();
  // HubDashboard a sa propre map cartographique plein écran → on masque
  // le fond map global (V14.68.6 "Vision Map Partout").
  useSuppressMapBackground();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [houseDialogOpen, setHouseDialogOpen] = useState(false);
  const [wizardPrefilledAddress, setWizardPrefilledAddress] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [portfolioOpen, setPortfolioOpen] = useState(true);
  const [fitAllNonce, setFitAllNonce] = useState(0);
  // Drop-pin flow (Google Maps-style): user picks an address OR clicks the map
  // → resolves to { lat, lon, display_name } → can create a project at this exact
  // location. Click on map = reverse-geocode reposition.
  const [droppedPin, setDroppedPin] = useState(null);
  // Phase 2 — Immersive Geodata Explorer
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [buildingFootprint, setBuildingFootprint] = useState(null);  // {found, polygon, area_m2, levels, ...}
  const [buildingLoading, setBuildingLoading] = useState(false);
  const [inspectionMode, setInspectionMode] = useState(false);  // Phase 2 — 3D extruded footprint
  const [projectedHeight, setProjectedHeight] = useState(null); // Phase 2.5 — slider override (m)
  const [cadastreParcels, setCadastreParcels] = useState(null); // Phase 3.A — array of {parcelle_id, polygon, ...}
  const [selectedParcelleId, setSelectedParcelleId] = useState(null);
  const [planFootprints, setPlanFootprints] = useState(null); // Sprint 3.B P2 — array of {project_id, polygon_latlon, ...}
  const portfolioPrevOpenRef = useRef(true);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || 'dark'; } catch { return 'dark'; }
  });
  const mapRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/projects/summary`, { withCredentials: true })
      .then(r => {
        const enriched = r.data.map(p => ({
          ...p,
          coords: p.coords || null,
          published: p.status === 'final',
        }));
        setProjects(enriched);
      })
      .catch(() => {});
  }, []);

  // Sprint 3.B P2 — Plan source footprints (apartment polygons cleaned via the editor)
  useEffect(() => {
    axios.get(`${API}/hub/plan-source-footprints`, { withCredentials: true })
      .then(r => setPlanFootprints(r.data?.items || []))
      .catch(() => setPlanFootprints([]));
  }, []);

  // Hydrate theme from user profile when available (server is the source of truth)
  useEffect(() => {
    if (user?.theme && (user.theme === 'dark' || user.theme === 'smooth') && user.theme !== theme) {
      setTheme(user.theme);
      try { localStorage.setItem(THEME_KEY, user.theme); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.theme]);

  const filtered = projects.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'published' ? p.published : !p.published);
    return matchSearch && matchFilter;
  });

  const pinnedPoints = filtered.filter(p => Array.isArray(p.coords) && p.coords.length === 2);
  const googleMapsKey = GOOGLE_MAPS_API_KEY;

  // Sélection projet (panel listing → carte 3D fly cinematic)
  const handleSelectProject = (project) => {
    setSelectedProjectId(project.project_id);
  };
  const handleMarker3DClick = (project) => {
    setSelectedProject(project);
    setSelectedProjectId(project.project_id);
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'smooth' : 'dark';
    setTheme(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
    // Persist server-side (best-effort, ignored if not authenticated)
    if (updateTheme) updateTheme(next);
  };

  // ==== Theme-aware tile URLs ====
  // Single-source-of-truth in /lib/tiles.js. GCP: inject REACT_APP_MAPBOX_TOKEN
  // via Cloud Build / Secret Manager at build time to switch to Mapbox styles.
  const darkTileUrl  = buildTileUrl('dark');
  const lightTileUrl = buildTileUrl('smooth');
  const tileAttr = buildAttribution(theme);

  // ===== Drop-pin flow handlers =====
  // 1) User picks an address result from the search → drop a pin there
  //    AND switch to the immersive Explorer Panel (closes Portfolio temporarily,
  //    fetches the OSM building footprint, opens the right-side details panel).
  const handleAddressResolved = (r) => {
    const pin = { lat: r.lat, lon: r.lon, display_name: r.display_name, source: 'search' };
    setDroppedPin(pin);
    // Remember portfolio state so we can restore it when the explorer closes
    portfolioPrevOpenRef.current = portfolioOpen;
    setPortfolioOpen(false);
    setExplorerOpen(true);
    setBuildingFootprint(null);
    setBuildingLoading(true);
    axios
      .get(`${API}/geocode/building`, {
        params: { lat: r.lat, lon: r.lon, radius: 40 },
        withCredentials: true,
        timeout: 15000,
      })
      .then((resp) => setBuildingFootprint(resp.data || { found: false }))
      .catch(() => setBuildingFootprint({ found: false }))
      .finally(() => setBuildingLoading(false));

    // Sprint 3.A — also fetch the cadastral parcel at this address and the
    // bbox-wide parcels for context (contextual layer activation).
    fetchCadastreAtPoint(r.lat, r.lon);
  };

  // Sprint 3.A — fetch cadastral parcels around (lat, lon).
  // 1) get the parcel containing the point (becomes the selected one)
  // 2) get all parcels in a ~120m × 120m bbox around it (for context layer)
  const fetchCadastreAtPoint = (lat, lon) => {
    const dLat = 0.0006; // ~67 m vertical
    const dLon = 0.0009; // ~67 m horizontal at 48° lat
    // Parcel at point
    axios
      .get(`${API}/cadastre/parcelle`, { params: { lat, lon }, withCredentials: true, timeout: 14000 })
      .then((resp) => {
        const p = resp.data?.parcelle;
        if (resp.data?.found && p?.parcelle_id) setSelectedParcelleId(p.parcelle_id);
      })
      .catch(() => {/* ignore — not blocking */});
    // bbox around the point
    axios
      .get(`${API}/cadastre/by-bbox`, {
        params: {
          north: lat + dLat, south: lat - dLat,
          east: lon + dLon, west: lon - dLon,
          limit: 80,
        },
        withCredentials: true,
        timeout: 14000,
      })
      .then((resp) => setCadastreParcels(resp.data?.items || []))
      .catch(() => setCadastreParcels([]));
  };
  // 2) User clicks anywhere on the map → reverse-geocode to find the postal address
  //    and reposition the drop pin (only active when a pin is already dropped, so a
  //    casual map click doesn't surprise the user with a random pin).
  const handleMapClick = async (lat, lng) => {
    if (!droppedPin) return; // require explicit "search first" intent to enable click-to-reposition
    // Optimistic: place pin immediately with rounded coords, then refine display_name
    setDroppedPin({ lat, lon: lng, display_name: `${lat.toFixed(5)}, ${lng.toFixed(5)}`, source: 'click' });
    // If the Explorer is open, also refresh the building footprint at the new location
    if (explorerOpen) {
      setBuildingLoading(true);
      setBuildingFootprint(null);
      axios
        .get(`${API}/geocode/building`, {
          params: { lat, lon: lng, radius: 40 },
          withCredentials: true,
          timeout: 15000,
        })
        .then((resp) => setBuildingFootprint(resp.data || { found: false }))
        .catch(() => setBuildingFootprint({ found: false }))
        .finally(() => setBuildingLoading(false));
    }
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'fr,en' } },
      );
      const j = await r.json();
      if (j && j.display_name) {
        setDroppedPin({ lat, lon: lng, display_name: j.display_name, source: 'click' });
      }
    } catch {/* keep optimistic coords-only label */}
  };
  // 3) User clicks "Créer un projet ici" → open wizard pre-filled with the drop pin
  const handleCreateAtPin = () => {
    if (!droppedPin) return;
    setWizardPrefilledAddress({
      lat: droppedPin.lat,
      lng: droppedPin.lon,
      display_name: droppedPin.display_name,
    });
    setWizardOpen(true);
  };
  // 4) Clear drop pin (X button on the card)
  const handleClearDropPin = () => setDroppedPin(null);

  // 5) Explorer panel close — restores Portfolio and clears the pin/footprint
  const handleExplorerClose = () => {
    setExplorerOpen(false);
    setBuildingFootprint(null);
    setBuildingLoading(false);
    setDroppedPin(null);
    setInspectionMode(false);
    setProjectedHeight(null);
    // Phase 3.A — clear cadastre layer too (contextual)
    setCadastreParcels(null);
    setSelectedParcelleId(null);
    if (portfolioPrevOpenRef.current) setPortfolioOpen(true);
  };

  return (
    <div
      className="hub-root h-full w-full flex flex-col overflow-hidden relative"
      data-testid="hub-dashboard"
      data-embedded="1"
      data-theme={theme}
      style={{ fontFamily: 'Manrope, sans-serif', background: theme === 'smooth' ? '#F7F4EE' : '#09090B' }}
    >
      <HudGrainOverlay />
      {/* Inline styles for grid layout aesthetics + smooth-theme overrides */}
      <style>{`
        .leaflet-container { background:#09090B !important; transition: background 700ms ease; }
        .hub-root[data-theme='smooth'] .leaflet-container { background:#F7F4EE !important; }
        /* Smooth crossfade between the two stacked tile layers when toggling theme */
        .leaflet-pane .leaflet-layer { transition: opacity 700ms ease-in-out; }
        .react-grid-item.react-grid-placeholder {
          background: rgba(217,160,91,0.18) !important;
          border: 1px dashed rgba(217,160,91,0.6) !important;
          border-radius: 16px !important;
          opacity: 1 !important;
        }
        .react-grid-item > .react-resizable-handle {
          opacity: 0;
          transition: opacity .2s ease;
        }
        .react-grid-item:hover > .react-resizable-handle { opacity: 0.7; }
        .react-grid-item > .react-resizable-handle::after {
          border-color: rgba(217,160,91,0.8) !important;
        }
        .leaflet-control-attribution {
          background: rgba(0,0,0,.45) !important;
          color: rgba(255,255,255,.4) !important;
          font-size: 9px !important;
        }
        .hub-root[data-theme='smooth'] .leaflet-control-attribution {
          background: rgba(255,255,255,.6) !important;
          color: rgba(26,26,26,.45) !important;
        }

        /* ==== SMOOTH THEME OVERRIDES — precise, surgical ==== */
        .hub-root[data-theme='smooth'] .smooth-topbar { background:#F7F4EE !important; border-color: rgba(26,26,26,0.08) !important; }
        .hub-root[data-theme='smooth'] .smooth-text { color: #1A1A1A !important; }
        .hub-root[data-theme='smooth'] .smooth-text-muted { color: rgba(26,26,26,0.65) !important; }
        .hub-root[data-theme='smooth'] .smooth-text-dim { color: rgba(26,26,26,0.40) !important; }
        .hub-root[data-theme='smooth'] .smooth-card {
          background: rgba(255,255,255,0.65) !important;
          border-color: rgba(26,26,26,0.10) !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.08) !important;
        }
        .hub-root[data-theme='smooth'] .smooth-card-strong {
          background: rgba(255,255,255,0.85) !important;
          border-color: rgba(26,26,26,0.10) !important;
        }
        .hub-root[data-theme='smooth'] .smooth-sidebar {
          background: rgba(255,255,255,0.75) !important;
          border-color: rgba(26,26,26,0.08) !important;
        }
        .hub-root[data-theme='smooth'] .smooth-divider { border-color: rgba(26,26,26,0.08) !important; }
        .hub-root[data-theme='smooth'] .smooth-input {
          background: rgba(0,0,0,0.04) !important;
          border-color: rgba(26,26,26,0.10) !important;
          color: #1A1A1A !important;
        }
        .hub-root[data-theme='smooth'] .smooth-input::placeholder { color: rgba(26,26,26,0.35) !important; }
        .hub-root[data-theme='smooth'] .smooth-tool-btn:hover { background: rgba(0,0,0,0.04) !important; color:#1A1A1A !important; }
        .hub-root[data-theme='smooth'] .smooth-locked { color: rgba(26,26,26,0.35) !important; }
        .hub-root[data-theme='smooth'] .smooth-handle { background: linear-gradient(to right, rgba(0,0,0,0.04), transparent) !important; border-color: rgba(26,26,26,0.08) !important; }
      `}</style>


      {/* Main */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Map + HUD overlay (no left sidebar — PortfolioPanel on the right is the single project source) */}
        <div className="flex-1 relative">
          {/* V14.68.18 — Floating slim topbar (address search + portfolio toggle) */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[600] w-[min(520px,60vw)]">
            <AddressGeocoderSearch onResult={handleAddressResolved} />
            {droppedPin && !explorerOpen && (
              <div className="mt-2">
                <LocationDropPinCard
                  pin={droppedPin}
                  onCreateProject={handleCreateAtPin}
                  onClear={handleClearDropPin}
                />
              </div>
            )}
          </div>
          <button
            onClick={() => navigate('/studio-exterieur')}
            data-testid="hub-exterior-studio-btn"
            title="Studio Extérieur — génération 3D depuis la carte (sandbox)"
            className="absolute top-3 right-[104px] z-[600] h-10 px-3 rounded-lg inline-flex items-center gap-1.5 text-[11.5px] font-semibold transition-colors backdrop-blur-md bg-black/70 border border-[#D9A05B]/40 hover:bg-black/85"
            style={{ color: '#D9A05B' }}
          >
            <Mountain className="w-3.5 h-3.5" /> Studio Extérieur
          </button>
          <button
            onClick={() => setPortfolioOpen(v => !v)}
            data-testid="hub-embedded-portfolio-toggle"
            className={`absolute top-3 right-3 z-[600] w-10 h-10 rounded-lg flex items-center justify-center transition-colors backdrop-blur-md ${
              portfolioOpen
                ? 'bg-[#D9A05B]/20 text-[#D9A05B] border border-[#D9A05B]/40'
                : 'bg-black/70 text-white/80 border border-white/15 hover:text-white hover:bg-black/85'
            }`}
            title={portfolioOpen ? 'Masquer le portfolio' : 'Afficher le portfolio'}
            aria-label="Portfolio"
          >
            {portfolioOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
          </button>

          {/* Floating "+" button — top-left, tooltip-on-hover */}
          <button
            onClick={() => setWizardOpen(true)}
            data-testid="hub-new-project-btn"
            title="Nouveau projet"
            aria-label="Nouveau projet"
            className="absolute left-4 top-4 z-[700] w-11 h-11 rounded-full bg-[#D9A05B] hover:bg-[#C78D46] text-black shadow-[0_8px_24px_rgba(217,160,91,0.45)] flex items-center justify-center transition-all hover:scale-105 active:scale-95 group"
          >
            <Plus className="w-5 h-5" strokeWidth={2.5} />
            <span className="absolute left-full ml-2 px-2.5 py-1.5 rounded-md bg-black/85 text-white text-[11px] font-semibold tracking-wide whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Nouveau projet
            </span>
          </button>

          {/* Sprint 22 — single-house creation CTA, just below the multi-lot "+" */}
          <button
            onClick={() => setHouseDialogOpen(true)}
            data-testid="hub-new-house-btn"
            title="Nouvelle maison unifamiliale"
            aria-label="Nouvelle maison unifamiliale"
            className="absolute left-4 top-[68px] z-[700] w-11 h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 shadow-[0_8px_24px_rgba(16,185,129,0.45)] flex items-center justify-center transition-all hover:scale-105 active:scale-95 group"
          >
            <Home className="w-5 h-5" strokeWidth={2.2} />
            <span className="absolute left-full ml-2 px-2.5 py-1.5 rounded-md bg-black/85 text-white text-[11px] font-semibold tracking-wide whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Nouvelle maison
            </span>
          </button>


          {/* "Vue d'ensemble" button — appears when a project is selected, fits map back to all pins */}
          {selectedProjectId && pinnedPoints.length > 0 && (
            <button
              onClick={() => {
                setSelectedProjectId(null);
                setSelectedProject(null);
                setFitAllNonce(n => n + 1);
              }}
              data-testid="hub-fit-all-btn"
              title="Revenir à la vue d'ensemble"
              className="absolute left-4 top-[68px] z-[700] h-9 px-3 rounded-full bg-black/70 hover:bg-black/85 text-white text-[11px] font-semibold tracking-wide flex items-center gap-1.5 border border-white/15 backdrop-blur-md transition-colors"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Vue d'ensemble
            </button>
          )}

          {/* Map 3D photoréaliste (Google) avec fallback Leaflet 2D si clé absente */}
          <Hub3DMap
            projects={pinnedPoints}
            selectedId={selectedProjectId}
            onMarkerClick={handleMarker3DClick}
            apiKey={googleMapsKey}
            fitAllNonce={fitAllNonce}
            flyTo={droppedPin ? { lat: droppedPin.lat, lon: droppedPin.lon, nonce: `${droppedPin.lat}|${droppedPin.lon}` } : null}
            dropPin={droppedPin}
            onMapClick={handleMapClick}
            cadastralPolygon={explorerOpen && buildingFootprint?.found ? buildingFootprint.polygon : null}
            cadastralHeight={explorerOpen && buildingFootprint?.found ? (projectedHeight ?? buildingFootprint.height_m) : null}
            inspectionMode={inspectionMode}
            cadastreParcels={explorerOpen ? cadastreParcels : null}
            selectedParcelleId={explorerOpen ? selectedParcelleId : null}
            onParcelClick={(pid) => setSelectedParcelleId(pid)}
            planFootprints={planFootprints}
            onFootprintClick={(pid) => {
              const p = projects.find((pr) => pr.project_id === pid);
              if (p) handleSelectProject(p);
            }}
            onNavigateDetails={(p) => navigate(`/projects/${p.project_id}`)}
            onNavigateMinisite={(p) => navigate(`/hub/viewer/${p.project_id}`)}
          />

          {/* Portfolio Management Panel (right sidebar) */}
          <PortfolioPanel
            projects={filtered}
            selectedId={selectedProjectId}
            onSelectProject={handleSelectProject}
            onDeselectProject={() => { setSelectedProjectId(null); setSelectedProject(null); setFitAllNonce(n => n + 1); }}
            onNavigateDetails={(p) => navigate(`/projects/${p.project_id}`)}
            onNavigateMinisite={(p) => navigate(`/hub/viewer/${p.project_id}`)}
            onClose={() => setPortfolioOpen(false)}
            visible={portfolioOpen && !explorerOpen}
            filter={filter}
            setFilter={setFilter}
            search={search}
            setSearch={setSearch}
          />

          {/* Phase 2 — Immersive Geodata Explorer (Address → Footprint → Create) */}
          {explorerOpen && (
            <ExplorerPanel
              pin={droppedPin}
              building={buildingFootprint}
              loading={buildingLoading}
              inspectionMode={inspectionMode}
              onToggleInspection={(next) => {
                setInspectionMode(next);
                // Seed the slider with the OSM height when first turning on
                if (next && projectedHeight == null && buildingFootprint?.height_m) {
                  setProjectedHeight(buildingFootprint.height_m);
                }
              }}
              projectedHeight={projectedHeight}
              onProjectedHeightChange={setProjectedHeight}
              parcelle={cadastreParcels?.find((p) => p.parcelle_id === selectedParcelleId) || null}
              onCreateProject={handleCreateAtPin}
              onClose={handleExplorerClose}
            />
          )}

          {/* Re-open Portfolio handle when collapsed */}
          {!portfolioOpen && (
            <button
              onClick={() => setPortfolioOpen(true)}
              data-testid="portfolio-reopen"
              title="Ouvrir le portfolio"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-[700] w-8 h-16 rounded-l-lg bg-black/75 hover:bg-black/90 text-white border-l border-y border-white/10 backdrop-blur-md flex items-center justify-center transition-colors"
            >
              <PanelRightOpen className="w-4 h-4" />
            </button>
          )}

        </div>
      </div>

      {/* New Project wizard (slide-from-right) */}
      <NewProjectWizard
        open={wizardOpen}
        onOpenChange={(v) => {
          setWizardOpen(v);
          if (!v) {
            // Clear the pre-filled state once the wizard closes (the wizard already
            // resets its internal state, but we also clear the drop pin so the
            // resolved location does not linger after a successful creation).
            setTimeout(() => {
              setWizardPrefilledAddress(null);
              setDroppedPin(null);
              setExplorerOpen(false);
              setBuildingFootprint(null);
              setInspectionMode(false);
              setProjectedHeight(null);
              if (portfolioPrevOpenRef.current) setPortfolioOpen(true);
            }, 350);
          }
        }}
        prefilledAddress={wizardPrefilledAddress}
      />

      {/* Sprint 22 — Single-house creation dialog */}
      <NewHouseDialog
        open={houseDialogOpen}
        onClose={() => setHouseDialogOpen(false)}
      />

      {/* Onboarding 5-step tour (auto-trigger for new users) */}
      <OnboardingDashboard
        onOpenWizard={() => setWizardOpen(true)}
      />
    </div>
  );
};

export default HubDashboard;
